import {ERestrictionAction, ICalendarRestriction} from '@ryndem/pqf-design-system-next-gen';
import {RestriccionFechasPreviewResponse} from '@ryndem/api-logistica';

/**
 * @description Enum representing the form control keys for the Delivery Restriction configuration form.
 * @enum {string}
 * @property {string} START_DATE - Key for the restriction start date control.
 * @property {string} END_DATE - Key for the restriction end date control.
 * @property {string} REPEAT - Key for the recurrence toggle control.
 * @property {string} NO_REPEAT - Key for the no-recurrence toggle control.
 * @property {string} FREQUENCY_TYPE - Key for the frequency type dropdown control.
 * @property {string} FINISH_DATE - Key for the recurrence end date control.
 * @property {string} FREQUENCY - Key for the recurrence frequency input control.
 * @property {string} MONTHLY_OPTION - Key for the monthly recurrence option dropdown control.
 */
export enum EDeliveryRestriction {
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  REPEAT = 'repeat',
  NO_REPEAT = 'noRepeat',
  FREQUENCY_TYPE = 'frequencyType',
  FINISH_DATE = 'finishDate',
  FREQUENCY = 'eachFrequency',
  MONTHLY_OPTION = 'monthlyOption',
}

/**
 * @description Enum representing the capitalized frequency type labels
 * as returned by the backend and used for display and comparison.
 * @enum {string}
 * @property {string} DIARIO - Daily frequency label.
 * @property {string} SEMANAL - Weekly frequency label.
 * @property {string} MENSUAL - Monthly frequency label.
 * @property {string} ANUAL - Annual frequency label.
 */
export enum ECapitalFrequency {
  DIARIO = 'Día',
  SEMANAL = 'Semana',
  MENSUAL = 'Mes',
  ANUAL = 'Año',
}

/**
 * @description Enum representing the context for the get and saved information
 * @enum {string}
 * @property {string} PROVIDER - context provider
 * @property {string} GENERAL - context general
 */
export enum EDeliveryRestrictionContext {
  PROVIDER = 'Proveedor',
  GENERAL = 'General',
}

/**
 * @description Interface representing all UI labels for the restriction configuration modal.
 * @property {string} buttonModalCalendarCancel - Label for the calendar cancel button.
 * @property {string} buttonModalCalendarAccept - Label for the calendar accept button.
 * @property {string} labelButtonCancel - Label for the modal cancel button.
 * @property {string} labelButtonSaved - Label for the add restriction button.
 * @property {string} labelButtonSavedEdit - Label for the save restriction button in edit mode.
 * @property {string} labelRequired - Label indicating a required field.
 * @property {string} labelSelectedDate - Label for the date picker field.
 * @property {string} labelNoRepeat - Label for the no-repeat radio option.
 * @property {string} labelRepeatEvery - Label for the repeat frequency input.
 * @property {string} labelRepeatFinish - Label for the recurrence end date field.
 * @property {string} labelTitleModal - Title for the add restriction modal.
 * @property {string} labelTitleModalEdit - Title for the edit restriction modal.
 * @property {IMonthlyOptionLabels} monthlyOptions - Labels for the monthly recurrence dropdown options.
 * @property {Record<number, string>} dayNames - Map of JS day index (0–6) to day name strings.
 * @property {Record<number, string>} weekOrdinals - Map of week number (1–4) to ordinal strings.
 */
export interface ILabelsConfigModal {
  buttonModalCalendarCancel: string;
  buttonModalCalendarAccept: string;
  labelButtonCancel: string;
  labelButtonSaved: string;
  labelButtonSavedEdit: string;
  labelRequired: string;
  labelSelectedDate: string;
  labelNoRepeat: string;
  labelRepeatEvery: string;
  labelRepeatFinish: string;
  labelTitleModal: string;
  labelTitleModalEdit: string;
  monthlyOptions: IMonthlyOptionLabels;
  dayNames: Record<number, string>;
  weekOrdinals: Record<number, string>;
}

/**
 * @description Interface representing the labels for the monthly recurrence dropdown options.
 * @property {string} eachMonthOnDay - Label for "every month on day X" option.
 * @property {string} eachMonthOn - Label for "every month on the Nth weekday" option.
 * @property {string} eachMonthLast - Label for "every month on the last weekday" option.
 */
export interface IMonthlyOptionLabels {
  eachMonthOnDay: string;
  eachMonthOn: string;
  eachMonthLast: string;
}

/**
 * @description Interface representing the labels for the delete confirmation modal.
 * @property {string} titleModalDelete - Title of the delete confirmation modal.
 * @property {string} firstText - First line of the confirmation message.
 * @property {string} secondText - Second line of the confirmation message.
 * @property {string} thirdText - Third line of the confirmation message (usually a question).
 * @property {string} buttonCancel - Label for the cancel button.
 * @property {string} buttonConfirm - Label for the confirm delete button.
 */
export interface ILabelsModalDelete {
  titleModalDelete: string;
  firstText: string;
  secondText: string;
  thirdText: string;
  buttonCancel: string;
  buttonConfirm: string;
}

/**
 * @description Interface representing the labels for the edit confirmation modal.
 * @property {string} titleModalEdit - Title of the edit confirmation modal.
 * @property {string} firstText - First line of the confirmation message.
 * @property {string} secondText - Second line of the confirmation message.
 * @property {string} thirdText - Third line of the confirmation message (usually a question).
 * @property {string} buttonCancel - Label for the cancel button.
 * @property {string} buttonConfirm - Label for the confirm changes button.
 */
export interface ILabelsModalEdit {
  titleModalEdit: string;
  firstText: string;
  secondText: string;
  thirdText: string;
  buttonCancel: string;
  buttonConfirm: string;
}

/**
 * @description Mapping from JavaScript day index (0–6, starting Sunday)
 * to backend day number (1–7, starting Monday).
 * Used to convert JS Date day values to the format expected by the backend.
 */
export const JS_DAY_TO_BACKEND: Record<number, number> = {
  0: 7, // Sunday
  1: 1, // Monday
  2: 2, // Tuesday
  3: 3, // Wednesday
  4: 4, // Thursday
  5: 5, // Friday
  6: 6, // Saturday
};

/**
 * @description Extended type for `RestriccionFechasPreviewResponse` that overrides `Fechas`
 * from `object` to `string[][]` and adds local control properties.
 * @property {string[][] | undefined} Fechas - Matrix of date range strings returned by the backend.
 * @property {boolean | undefined} NewConfiguration - `true` if this restriction has not been persisted yet.
 * @property {string | undefined} IdConfigRestriction - Local identifier used to track and match restrictions across state nodes.
 * @property {boolean | undefined} EditMode - `true` if this restriction is currently being edited.
 */
export type RestriccionFechasPreviewExtended = Omit<RestriccionFechasPreviewResponse, 'Fechas'> & {
  Fechas?: string[][];
  NewConfiguration?: boolean;
  IdConfigRestriction?: string;
  isEditMode?: boolean;
};

/**
 * @description Extended interface for `ICalendarRestriction` that adds local control properties
 * not present in the base library interface.
 * @property {string | undefined} IdConfigRestriction - Unique identifier for tracking the restriction across state nodes.
 * @property {boolean | undefined} NewConfiguration - `true` if this restriction has not been persisted yet.
 */
export interface ICalendarRestrictionExtended extends ICalendarRestriction {
  IdConfigRestriction?: string;
  NewConfiguration?: boolean;
}

/**
 * @description Extended interface for the restriction card event,
 * using `ICalendarRestrictionExtended` instead of the base `ICalendarRestriction`
 * to support local control properties like `IdConfigRestriction` and `NewConfiguration`.
 * @property {number} index - The index of the restriction in the list.
 * @property {ERestrictionAction} action - The action performed on the restriction (`EDIT` or `DELETE`).
 * @property {ICalendarRestrictionExtended} restriction - The restriction object associated with the event.
 */
export interface IRestrictionCardEventExtend {
  index: number;
  action: ERestrictionAction;
  restriction: ICalendarRestrictionExtended;
}
